import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../helpers/api";
import { UpgradePlanSkeleton } from "../components/skeleton/PageSkeletons";
import { ToastFeedback } from "../components/ToastNotifications";
import "./UpgradePlan.css";
import { Can } from "../components/AccessContext";

const money = (amount, currency = "INR") => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency,
  maximumFractionDigits: 2
}).format((Number(amount) || 0) / 100);

const dateLabel = (value) => value
  ? new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric" }).format(new Date(value))
  : "Not available yet";

const ensureRazorpayScript = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const existing = document.querySelector('script[data-razorpay-checkout="true"]');
  if (existing) {
    existing.addEventListener("load", resolve, { once: true });
    existing.addEventListener("error", reject, { once: true });
    return;
  }
  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.async = true;
  script.dataset.razorpayCheckout = "true";
  script.onload = resolve;
  script.onerror = () => reject(new Error("Secure checkout could not be loaded. Please try again."));
  document.body.appendChild(script);
});

// Hosted checkouts (CCAvenue, Paytm) expect a browser form POST to the gateway.
const submitGatewayForm = ({ url, fields }) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  form.style.display = "none";
  Object.entries(fields || {}).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

const readPaymentReturn = () => {
  const params = new URLSearchParams(window.location.search);
  const reference = params.get("payment");
  return /^[A-Z0-9]{8,40}$/.test(reference || "") ? { reference, cancelled: params.get("cancelled") === "1" } : null;
};

// Onboarding and marketing links can preselect a plan: /upgrade-plan?plan=growth&interval=annual.
const readRequestedPlan = () => {
  const params = new URLSearchParams(window.location.search);
  const plan = params.get("plan");
  if (!/^[a-z0-9-]{1,80}$/.test(plan || "")) return null;
  return { plan, interval: params.get("interval") === "annual" ? "annual" : "monthly" };
};

const isHttpsUrl = (value) => typeof value === "string" && value.startsWith("https://");

const formatNumber = (value) => new Intl.NumberFormat().format(Number(value) || 0);

// Plans can be offered monthly, annually, or both (older API responses omit the field: both).
const offersInterval = (plan, interval) => !Array.isArray(plan.billingIntervals) || plan.billingIntervals.includes(interval);

const planFeatureValue = (definition, plan) => {
  const value = plan.entitlements?.[definition.key];
  if (value === null || value === undefined) return "Existing access";
  if (definition.type === "boolean") return value ? "Included" : "Not included";
  return value == null ? "No configured cap" : `${new Intl.NumberFormat().format(value)} ${definition.unit}`;
};

const UpgradePlan = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestedPlan = useRef(readRequestedPlan());
  const [billingInterval, setBillingInterval] = useState(() => requestedPlan.current?.interval || "monthly");
  const [selectedPlan, setSelectedPlan] = useState(() => requestedPlan.current?.plan || "");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [busyPlan, setBusyPlan] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const returnHandled = useRef(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest("/api/subscriptions");
      setData(result);
      setSelectedPlan((current) => (current && result.plans.some((plan) => plan.slug === current) ? current : "") || result.current?.plan?.slug || result.plans[0]?.slug || "");
    } catch (requestError) {
      setError(requestError.message || "Unable to load your subscription.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // A preselected plan opens straight at its checkout summary; the query string is then cleared.
  useEffect(() => {
    if (!data || !requestedPlan.current) return;
    const requested = requestedPlan.current;
    requestedPlan.current = null;
    if (!readPaymentReturn()) window.history.replaceState(null, "", window.location.pathname);
    if (!data.plans.some((plan) => plan.slug === requested.plan)) return;
    window.requestAnimationFrame?.(() => document.getElementById("checkout-summary-heading")?.scrollIntoView?.({ behavior: "smooth", block: "start" }));
  }, [data]);

  const selected = data?.plans.find((plan) => plan.slug === selectedPlan);
  const current = data?.current;
  const activePaid = current?.plan?.monthlyAmount > 0 && current?.status === "active";
  const pendingCheckout = data?.pendingCheckout;
  const price = selected ? (billingInterval === "annual" ? selected.annualAmount : selected.monthlyAmount) : 0;
  const quote = useMemo(() => {
    if (appliedCoupon && appliedCoupon.planSlug === selectedPlan && appliedCoupon.interval === billingInterval) return appliedCoupon;
    const taxPercent = Number(data?.taxPercent) || 0;
    const taxAmount = Math.round(price * taxPercent / 100);
    return {
      originalAmount: price,
      discountAmount: 0,
      taxAmount,
      taxPercent,
      totalAmount: price + taxAmount,
      currency: selected?.currency || "INR"
    };

  }, [appliedCoupon, billingInterval, data?.taxPercent, price, selected?.currency, selectedPlan]);

  const clearQuote = () => {
    setAppliedCoupon(null);
    setNotice("");
    setError("");
  };

  const applyCoupon = async (event) => {
    event.preventDefault();
    if (!selected || !couponCode.trim()) return;
    setCouponBusy(true);
    setError("");
    setNotice("");
    try {
      const validated = await apiRequest("/api/subscriptions/coupon/validate", {
        method: "POST",
        body: JSON.stringify({ planSlug: selected.slug, interval: billingInterval, code: couponCode.trim() })
      });
      setAppliedCoupon({ ...validated, planSlug: selected.slug, interval: billingInterval });
      setCouponCode(validated.coupon.code);
      setNotice(`${validated.coupon.code} applied to this selection.`);
    } catch (requestError) {
      setAppliedCoupon(null);
      setError(requestError.message || "This coupon could not be applied.");
    } finally {
      setCouponBusy(false);
    }
  };

  const cancelUnfinishedCheckout = async (reference) => {
    try {
      await apiRequest(`/api/subscriptions/payments/${reference}/cancel`, { method: "POST", body: JSON.stringify({}) });
    } catch (requestError) {
      setError(requestError.message || "Unable to cancel the pending checkout.");
    }
  };

  const cancelPendingSubscription = async () => {
    if (!pendingCheckout?.id) return;
    setBusyPlan("cancel-pending");
    setError("");
    setNotice("");
    try {
      const result = await apiRequest(`/api/subscriptions/checkout/${pendingCheckout.id}/cancel`, { method: "POST", body: JSON.stringify({}) });
      setNotice(result.message);
      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to cancel pending checkout.");
    } finally {
      setBusyPlan("");
    }
  };

  // The server asks the gateway for the real status; a redirect or query string never activates a plan.
  const verifyPayment = useCallback(async (reference, providerResponse, attempt = 0) => {
    setError("");
    setNotice("Verifying your payment securely with the payment gateway…");
    try {
      const result = await apiRequest(`/api/subscriptions/payments/${reference}/verify`, {
        method: "POST",
        body: JSON.stringify(providerResponse ? { providerResponse } : {})
      });
      if (result.status === "pending" && attempt < 5) {
        setNotice(result.message);
        window.setTimeout(() => verifyPayment(reference, undefined, attempt + 1), 4000);
        return;
      }
      setNotice(result.message);
      await loadData();
    } catch (verificationError) {
      setNotice("");
      // Reload first: loadData clears the error banner when it starts.
      await loadData();
      setError(verificationError.message || "Payment verification failed. Your plan will not change until the gateway confirms payment.");
    }
  }, [loadData]);

  useEffect(() => {
    if (returnHandled.current) return;
    const returned = readPaymentReturn();
    if (!returned) return;
    returnHandled.current = true;
    window.history.replaceState(null, "", window.location.pathname);
    if (returned.cancelled) {
      cancelUnfinishedCheckout(returned.reference).then(() => {
        setNotice("Checkout cancelled. Your plan has not changed.");
        loadData();
      });
    } else {
      verifyPayment(returned.reference);
    }
  }, [loadData, verifyPayment]);

  const openRazorpay = async (checkout, description) => {
    await ensureRazorpayScript();
    const { action } = checkout;
    let checkoutFinished = false;
    const modal = new window.Razorpay({
      key: action.keyId,
      ...(action.subscriptionId ? { subscription_id: action.subscriptionId } : { order_id: action.orderId, amount: action.amount, currency: action.currency }),
      name: "Socialyorix",
      description,
      prefill: action.prefill || data?.customer,
      theme: { color: "#087e75" },
      handler: async (response) => {
        checkoutFinished = true;
        await verifyPayment(checkout.reference, response);
      },
      modal: {
        ondismiss: async () => {
          if (!checkoutFinished) {
            await cancelUnfinishedCheckout(checkout.reference);
            setNotice("");
            await loadData();
          }
        }
      }
    });
    modal.on("payment.failed", (event) => {
      setError(event.error?.description || "Payment was not completed. You can try another payment method.");
    });
    modal.open();
  };

  // Runs whatever the active gateway asked for: Razorpay's modal, a redirect, or a hosted form POST.
  const runCheckoutAction = async (checkout, description) => {
    const { action } = checkout;
    if (action?.type === "razorpay") return openRazorpay(checkout, description);
    if ((action?.type === "redirect" || action?.type === "form_post") && isHttpsUrl(action.url)) {
      setNotice(`Redirecting to ${checkout.providerName} secure checkout…`);
      if (action.type === "redirect") window.location.assign(action.url);
      else submitGatewayForm(action);
      return undefined;
    }
    throw new Error("Secure checkout could not be opened. Please try again.");
  };

  const startCheckout = async (plan) => {
    const checkout = await apiRequest("/api/subscriptions/checkout", {
      method: "POST",
      body: JSON.stringify({
        planSlug: plan.slug,
        interval: billingInterval,
        code: appliedCoupon?.planSlug === plan.slug && appliedCoupon.interval === billingInterval ? appliedCoupon.coupon.code : undefined
      })
    });
    try {
      await runCheckoutAction(checkout, `${plan.name} · ${billingInterval === "annual" ? "Annual" : "Monthly"} billing`);
    } catch (checkoutError) {
      await cancelUnfinishedCheckout(checkout.reference);
      throw checkoutError;
    }
  };

  const renewNow = async () => {
    setError("");
    setNotice("");
    setBusyPlan("renew");
    try {
      const checkout = await apiRequest("/api/subscriptions/renew", { method: "POST", body: JSON.stringify({}) });
      try {
        await runCheckoutAction(checkout, `${data.current.plan.name} renewal`);
      } catch (checkoutError) {
        await cancelUnfinishedCheckout(checkout.reference);
        throw checkoutError;
      }
    } catch (requestError) {
      setError(requestError.message || "Unable to start the renewal payment.");
    } finally {
      setBusyPlan("");
    }
  };

  const selectPlan = async (plan) => {
    setSelectedPlan(plan.slug);
    setError("");
    setNotice("");
  };

  const confirmPlanSelection = async () => {
    if (!selected) return;
    setError("");
    setNotice("");
    setBusyPlan(selected.slug);
    try {
      if (activePaid && selected.monthlyAmount === 0) {
        const result = await apiRequest("/api/subscriptions/cancel", { method: "POST", body: JSON.stringify({}) });
        setNotice("Your current plan will stay active until renewal; then your account will continue on the free plan. " + result.message);
        await loadData();
      } else if (activePaid) {
        const result = await apiRequest("/api/subscriptions/plan-change", {
          method: "POST",
          body: JSON.stringify({
            planSlug: selected.slug,
            interval: billingInterval,
            code: appliedCoupon?.planSlug === selected.slug && appliedCoupon.interval === billingInterval ? appliedCoupon.coupon.code : undefined
          })
        });
        setNotice(result.message);
        await loadData();
      } else if (selected.monthlyAmount > 0) {
        await startCheckout(selected);
      }
    } catch (requestError) {
      setError(requestError.message || "Unable to update your plan.");
    } finally {
      setBusyPlan("");
    }
  };

  const cancelRenewal = async () => {
    setError("");
    setNotice("");
    setBusyPlan("cancel");
    try {
      const result = await apiRequest("/api/subscriptions/cancel", { method: "POST", body: JSON.stringify({}) });
      setNotice(result.message);
      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to cancel renewal.");
    } finally {
      setBusyPlan("");
    }
  };

  if (loading && !data) {
    return <section className="upgrade-page"><UpgradePlanSkeleton /></section>;
  }
  if (!data) {
    return <section className="upgrade-page"><ToastFeedback type="error" message={error || "Subscription information is unavailable."} /><button type="button" onClick={loadData}>Try again</button></section>;
  }

  const currentPlan = current.plan;
  const currentPeriodLabel = current.currentPeriodEnd ? dateLabel(current.currentPeriodEnd) : "No renewal date yet";
  const canManage = current?.plan?.monthlyAmount > 0 && ["active", "past_due", "halted"].includes(current.status) && !current.cancelAtCycleEnd;
  const isCurrentSelection = selected?.slug === currentPlan.slug && billingInterval === current.billingInterval;
  const checkoutAvailable = data.checkout?.available !== false;
  const gatewayName = data.checkout?.providerName || "our payment partner";
  const pendingRenewal = data.pendingRenewal;
  // Usage items arrive keyed by counter; older responses carry only { used, limit }, so labels fall back to definitions.
  const usageItems = Object.entries(data.usage || {}).map(([usageKey, item]) => {
    const definition = data.featureDefinitions?.find((candidate) => candidate.usageKey === usageKey || candidate.key === item.key);
    const limit = item.limit ?? null;
    return {
      key: item.key || usageKey,
      label: item.label || definition?.label || usageKey,
      unit: item.unit || definition?.unit || "",
      used: Number(item.used) || 0,
      limit,
      remaining: item.remaining ?? (limit === null ? null : Math.max(0, limit - (Number(item.used) || 0))),
      reached: item.reached ?? (limit !== null && (Number(item.used) || 0) >= limit),
      resets: Boolean(item.resets)
    };
  });
  const reachedUsage = usageItems.filter((item) => item.reached);
  const visiblePlans = data.plans.filter((plan) => offersInterval(plan, billingInterval));
  const renderAction = (plan) => {
    if (plan.slug === currentPlan.slug && current.billingInterval === billingInterval && !current.scheduledPlan) {
      return <button className="upgrade-plan-action is-current" type="button" disabled>{current.cancelAtCycleEnd ? "Renewal cancelled" : "Current plan"}</button>;
    }
    if (current.scheduledPlan?.slug === plan.slug) {
      return <button className="upgrade-plan-action is-current" type="button" disabled>Change scheduled</button>;
    }
    const unavailable = Boolean(pendingCheckout || current.cancelAtCycleEnd || current.scheduledPlan || (current.status !== "active" && currentPlan.monthlyAmount > 0));
    const label = unavailable && pendingCheckout ? "Finish or cancel pending checkout" : plan.slug === selectedPlan ? "Selected" : activePaid ? "Choose for renewal" : plan.monthlyAmount === 0 ? "Select free plan" : "Select plan";
    return (
      <button
        className={`upgrade-plan-action${plan.recommended ? " is-primary" : ""}${plan.slug === selectedPlan ? " is-selected" : ""}`}
        type="button"
        disabled={unavailable || busyPlan !== ""}
        onClick={() => selectPlan(plan)}
      >
        {label}
      </button>
    );
  };

  return (
    <section className="upgrade-page">
      <div className="upgrade-heading">
        <div>
          <p className="upgrade-eyebrow">PLANS &amp; BILLING</p>
          <h1>Plans that grow with your business</h1>
          <p className="upgrade-intro">Choose the tools and monthly capacity that fit the way you work.</p>
        </div>
        <div className="upgrade-secure-note"><i className="ti ti-shield-check" aria-hidden="true" /> {checkoutAvailable ? `Secure billing with ${gatewayName}` : "Secure billing"}{data.checkout?.testMode && <span className="upgrade-test-badge">TEST MODE</span>}</div>
      </div>

      {!checkoutAvailable && (
        <section className="upgrade-checkout-pending upgrade-unavailable" role="status" aria-label="Payments unavailable">
          <div><i className="ti ti-credit-card-off" aria-hidden="true" /><span><strong>Online payments are temporarily unavailable.</strong> You can keep using your current plan; purchases and upgrades will reopen shortly. Contact support if you need help.</span></div>
        </section>
      )}

      {error && <ToastFeedback type="error" message={error} />}
      {notice && <ToastFeedback type={notice.startsWith("Verifying your payment") ? "info" : "success"} message={notice} />}

      {pendingCheckout && (
        <section className="upgrade-checkout-pending" aria-label="Pending checkout">
          <div><i className="ti ti-clock-hour-4" aria-hidden="true" /><span>Payment for <strong>{pendingCheckout.plan.name}</strong> is {pendingCheckout.status === "authenticated" ? `being confirmed by ${pendingCheckout.providerName}` : "awaiting confirmation"}. Due: <strong>{money(pendingCheckout.totalAmount, pendingCheckout.currency)}</strong>{pendingCheckout.couponCode ? ` · Coupon ${pendingCheckout.couponCode} applied` : ""}. Your active plan will not change until payment is verified.</span></div>
          <div className="upgrade-pending-actions">
            {pendingCheckout.reference && <Can permission="user.billing.create"><button className="upgrade-link-button" type="button" disabled={busyPlan !== ""} onClick={() => verifyPayment(pendingCheckout.reference)}>Check payment status</button></Can>}
            <Can permission="user.billing.create"><button className="upgrade-link-button" type="button" disabled={busyPlan !== ""} onClick={cancelPendingSubscription}>{busyPlan === "cancel-pending" ? "Cancelling…" : "Cancel pending checkout"}</button></Can>
          </div>
        </section>
      )}

      {pendingRenewal && (
        <section className="upgrade-checkout-pending" aria-label="Pending renewal">
          <div><i className="ti ti-clock-hour-4" aria-hidden="true" /><span>A renewal payment of <strong>{money(pendingRenewal.amount, pendingRenewal.currency)}</strong> with {pendingRenewal.providerName} is awaiting confirmation.</span></div>
          <div className="upgrade-pending-actions">
            <Can permission="user.billing.create"><button className="upgrade-link-button" type="button" disabled={busyPlan !== ""} onClick={() => verifyPayment(pendingRenewal.reference)}>Check payment status</button></Can>
            <Can permission="user.billing.create"><button className="upgrade-link-button" type="button" disabled={busyPlan !== ""} onClick={async () => { await cancelUnfinishedCheckout(pendingRenewal.reference); await loadData(); }}>Cancel renewal payment</button></Can>
          </div>
        </section>
      )}

      <section className="upgrade-current" aria-labelledby="current-plan-heading">
        <div className="upgrade-current-main">
          <span className="upgrade-current-icon"><i className="ti ti-sparkles" aria-hidden="true" /></span>
          <div>
            <p className="upgrade-eyebrow">YOUR SUBSCRIPTION</p>
            <h2 id="current-plan-heading">{currentPlan.name}</h2>
            <p>{current.status.replaceAll("_", " ")} · {current.billingInterval} billing</p>
          </div>
        </div>
        <div className="upgrade-current-details">
          <div><span>{current.cancelAtCycleEnd ? "Access through" : "Next renewal"}</span><strong>{currentPeriodLabel}</strong></div>
          {current.currentPeriodStart && <div><span>Billing started</span><strong>{dateLabel(current.currentPeriodStart)}</strong></div>}
          {current.couponCode && <div><span>Applied coupon</span><strong>{current.couponCode}</strong></div>}
          {current.canRenew && !pendingRenewal && <Can permission="user.subscriptions.update"><button className="upgrade-checkout-action upgrade-renew-action" type="button" disabled={busyPlan !== "" || !checkoutAvailable} onClick={renewNow}>{busyPlan === "renew" ? "Starting renewal…" : `Renew now · ${money(current.renewalAmount, currentPlan.currency)}`}</button></Can>}
          {canManage && <Can permission="user.subscriptions.change_status"><button className="upgrade-link-button" type="button" disabled={busyPlan !== ""} onClick={cancelRenewal}>{busyPlan === "cancel" ? "Cancelling…" : "Cancel renewal"}</button></Can>}
        </div>
        {current.billingMode === "one_time" && current.currentPeriodEnd && !current.cancelAtCycleEnd && <p className="upgrade-scheduled-note">This plan doesn't renew automatically. {current.canRenew ? `Renew before ${currentPeriodLabel} to keep your access.` : `A Renew button appears 7 days before ${currentPeriodLabel}.`}</p>}
        {current.scheduledPlan && <p className="upgrade-scheduled-note">Switching to {current.scheduledPlan.name} ({current.scheduledBillingInterval}) after {currentPeriodLabel}. Your current plan remains active until then.</p>}
        {current.cancelAtCycleEnd && <p className="upgrade-scheduled-note">Renewal is off. Your current plan remains active through {currentPeriodLabel}.</p>}
      </section>

      <section className="upgrade-usage" aria-label="Current plan usage">
        <div className="upgrade-usage-heading"><div><h2>Plan usage</h2><p>{data.usagePeriod?.resetsAt ? `Monthly quotas reset on ${dateLabel(data.usagePeriod.resetsAt)}, in line with your billing cycle.` : "Monthly quotas reset with your billing cycle."} Connected accounts, automation rules and storage show what you're using now.</p></div></div>
        {reachedUsage.length > 0 && (
          <div className="upgrade-limit-banner" role="status">
            <i className="ti ti-alert-triangle" aria-hidden="true" />
            <span><strong>You've reached your plan limit for {reachedUsage.map((item) => item.label.toLowerCase()).join(", ")}.</strong> Upgrade your plan to keep going{reachedUsage.some((item) => item.resets) ? ", or wait for your monthly quota to reset" : ""}.</span>
            <button type="button" className="upgrade-link-button" onClick={() => document.getElementById("choose-plan-heading")?.scrollIntoView?.({ behavior: "smooth", block: "start" })}>View plans</button>
          </div>
        )}
        <div className="upgrade-usage-grid">
          {usageItems.map((item) => {
            const percent = item.limit ? Math.min(100, item.used / item.limit * 100) : item.limit === 0 ? 100 : 0;
            const status = item.limit === null ? "No limit" : item.reached ? "Limit reached" : `${formatNumber(item.remaining)} ${item.unit} left`;
            return (
              <div className={`upgrade-usage-item${item.reached ? " is-reached" : ""}`} key={item.key}>
                <div><span>{item.label}</span><strong>{formatNumber(item.used)} <small>/ {item.limit === null ? "∞" : formatNumber(item.limit)}</small></strong></div>
                <div className="upgrade-usage-track" role="progressbar" aria-label={item.label} aria-valuemin="0" aria-valuemax={Math.max(1, item.limit || 0)} aria-valuenow={Math.min(item.used, Math.max(1, item.limit || 0))}><span style={{ width: `${percent}%` }} /></div>
                <small className="upgrade-usage-status">{status}{item.resets && item.limit !== null ? " · resets monthly" : ""}</small>
              </div>
            );
          })}
        </div>
      </section>

      <div className="upgrade-plan-header">
        <div><p className="upgrade-eyebrow">SIMPLE, TRANSPARENT PRICING</p><h2 id="choose-plan-heading">Choose your plan</h2></div>
        <div className="upgrade-interval" role="group" aria-label="Billing interval">
          <button type="button" className={billingInterval === "monthly" ? "is-active" : ""} aria-pressed={billingInterval === "monthly"} onClick={() => { setBillingInterval("monthly"); clearQuote(); }}>Monthly</button>
          <button type="button" className={billingInterval === "annual" ? "is-active" : ""} aria-pressed={billingInterval === "annual"} onClick={() => { setBillingInterval("annual"); clearQuote(); }}>Annual</button>
        </div>
      </div>
      {billingInterval === "annual" && <p className="upgrade-annual-note">Annual plans are billed once a year. Savings are shown on each plan compared with paying monthly.</p>}
      {visiblePlans.length < data.plans.length && <p className="upgrade-annual-note">Some plans are only offered with {billingInterval === "annual" ? "monthly" : "annual"} billing.</p>}

      <div className="upgrade-plan-grid">
        {visiblePlans.map((plan) => {
          const amount = billingInterval === "annual" ? plan.annualAmount : plan.monthlyAmount;
          const isCurrent = plan.slug === currentPlan.slug;
          const saving = plan.monthlyAmount * 12 - plan.annualAmount;
          return (
            <article className={`upgrade-plan-card${plan.recommended ? " is-recommended" : ""}${isCurrent ? " is-current" : ""}`} key={plan.id}>
              {plan.recommended && <span className="upgrade-recommended">RECOMMENDED</span>}
              {isCurrent && <span className="upgrade-current-badge"><i className="ti ti-check" aria-hidden="true" /> CURRENT PLAN</span>}
              <h3>{plan.name}</h3>
              <p className="upgrade-plan-description">{plan.description}</p>
              <div className="upgrade-plan-price">
                <strong>{amount === 0 ? "Free" : money(amount, plan.currency)}</strong>
                {amount > 0 && <span>/ {billingInterval === "annual" ? "year" : "month"}</span>}
              </div>
              {billingInterval === "annual" && amount > 0 && <p className="upgrade-saving">{saving > 0 ? `Save ${money(saving, plan.currency)} per year` : "No annual savings"}</p>}
              <p className="upgrade-billing-caption">{amount === 0 ? "No payment required" : billingInterval === "annual" ? "Billed once a year · renews annually" : "Billed monthly · renews monthly"}</p>
              <div className="upgrade-plan-feature-count">{plan.features.slice(0, 2).join(" · ") || "Plan features"}</div>
              <ul className="upgrade-feature-list">
                {data.featureDefinitions?.map((definition) => <li key={definition.key}><i className={`ti ${definition.type === "boolean" && !plan.entitlements?.[definition.key] ? "ti-minus" : "ti-check"}`} aria-hidden="true" /><span>{definition.label}: {planFeatureValue(definition, plan)}</span></li>)}
                {plan.features.map((feature) => <li key={feature}><i className="ti ti-check" aria-hidden="true" /><span>{feature}</span></li>)}
              </ul>
              {renderAction(plan)}
            </article>
          );
        })}
      </div>

      {selected && ((selected.monthlyAmount > 0 && (!activePaid || !isCurrentSelection)) || (activePaid && selected.monthlyAmount === 0)) && (
        <section className="upgrade-checkout-panel" aria-labelledby="checkout-summary-heading">
          <div className="upgrade-checkout-title"><div><p className="upgrade-eyebrow">{activePaid ? "PLAN CHANGE SUMMARY" : "CHECKOUT SUMMARY"}</p><h2 id="checkout-summary-heading">{selected.name} · {billingInterval}</h2></div><span>{selected.currency} · taxes are itemized below</span></div>
          {selected.monthlyAmount > 0 && <form className="upgrade-coupon-form" onSubmit={applyCoupon}>
            <label htmlFor="upgrade-coupon">Have a coupon?</label>
            <div className="upgrade-coupon-controls">
              <input id="upgrade-coupon" value={couponCode} onChange={(event) => { setCouponCode(event.target.value); if (appliedCoupon) setAppliedCoupon(null); }} placeholder="Enter coupon code" autoComplete="off" maxLength={64} />
              {appliedCoupon ? <button type="button" className="upgrade-coupon-remove" onClick={() => { setAppliedCoupon(null); setCouponCode(""); setNotice(""); }}>Remove</button> : <Can permission="user.billing.create"><button type="submit" disabled={couponBusy || !couponCode.trim()}>{couponBusy ? "Checking…" : "Apply code"}</button></Can>}
            </div>
          </form>}
          <div className="upgrade-price-breakdown">
            <div><span>Original price</span><strong>{money(quote.originalAmount, quote.currency)}</strong></div>
            <div className={quote.discountAmount ? "has-discount" : ""}><span>Coupon discount{appliedCoupon ? ` · ${appliedCoupon.coupon.code}` : ""}</span><strong>−{money(quote.discountAmount, quote.currency)}</strong></div>
            <div><span>Tax ({quote.taxPercent || 0}%)</span><strong>{money(quote.taxAmount, quote.currency)}</strong></div>
            <div className="upgrade-total"><span>Due each {billingInterval === "annual" ? "year" : "month"}</span><strong>{money(quote.totalAmount, quote.currency)}</strong></div>
          </div>
          {selected.monthlyAmount > 0 && <p className="upgrade-checkout-disclaimer">{activePaid ? "Plan changes take effect at your next renewal; there is no proration." : `${gatewayName} securely collects your payment${data.checkout?.recurring ? " and renews it automatically" : " for one billing period, and you renew each period"}. Your plan activates only after the payment is confirmed.`}</p>}
          <Can permission={["user.billing.create", "user.subscriptions.update"]}><button className="upgrade-checkout-action" type="button" onClick={confirmPlanSelection} disabled={busyPlan !== "" || Boolean(pendingCheckout) || (!checkoutAvailable && selected.monthlyAmount > 0 && !activePaid) || current.cancelAtCycleEnd || Boolean(current.scheduledPlan) || (currentPlan.monthlyAmount > 0 && current.status !== "active") || (selected.monthlyAmount > 0 && quote.totalAmount <= 0)}>
            {busyPlan === selected.slug ? "Please wait…" : activePaid && selected.monthlyAmount === 0 ? "Switch to free at renewal" : activePaid ? "Schedule plan change" : checkoutAvailable ? "Continue to secure checkout" : "Payments unavailable"}
          </button></Can>
        </section>
      )}

      <section className="upgrade-comparison" aria-labelledby="plan-comparison-heading">
        <div className="upgrade-section-heading"><div><p className="upgrade-eyebrow">AT A GLANCE</p><h2 id="plan-comparison-heading">Compare plans</h2></div></div>
        <div className="upgrade-table-scroll">
          <table>
            <thead><tr><th scope="col">Included</th>{data.plans.map((plan) => <th scope="col" key={plan.id}>{plan.name}</th>)}</tr></thead>
            <tbody>
              {data.featureDefinitions?.map((definition) => (
                <tr key={definition.key}><th scope="row">{definition.label}</th>{data.plans.map((plan) => <td key={plan.id}>{planFeatureValue(definition, plan)}</td>)}</tr>
              ))}
              <tr><th scope="row">Annual savings</th>{data.plans.map((plan) => {
                const savings = plan.monthlyAmount * 12 - plan.annualAmount;
                return <td key={plan.id}>{money(savings, plan.currency)}</td>;
              })}</tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="upgrade-history" aria-labelledby="payment-history-heading">
        <div className="upgrade-section-heading"><div><p className="upgrade-eyebrow">BILLING ACTIVITY</p><h2 id="payment-history-heading">Payment history</h2></div></div>
        {data.payments.length ? (
          <div className="upgrade-payment-list">
            {data.payments.map((payment) => (
              <div className="upgrade-payment-row" key={payment.id}>
                <span className={`upgrade-payment-icon is-${payment.status}`}><i className="ti ti-receipt" aria-hidden="true" /></span>
                <div><strong>{money(payment.amount, payment.currency)}</strong><span>{dateLabel(payment.paidAt || payment.createdAt)}{payment.providerName ? ` · ${payment.providerName}` : ""} · {payment.providerPaymentId}{payment.refundedAmount > 0 && payment.status !== "refunded" ? ` · ${money(payment.refundedAmount, payment.currency)} refunded` : ""}</span></div>
                <span className={`upgrade-payment-status is-${payment.status}`}>{payment.status}</span>
              </div>
            ))}
          </div>
        ) : <p className="upgrade-empty-history">Your verified payments and invoices will appear here.</p>}
      </section>
      <p className="upgrade-footer-note">Card and payment details are handled by our payment gateway and are never stored by Socialyorix. Taxes are shown at the rate configured for this account.</p>
    </section>
  );
};

export default UpgradePlan;
